import { useEffect, useCallback, useRef } from 'react'
import './layout.scss'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import useInstalledApp from '../../../hooks/useInstalledApp'
import useMetrics from '../../../hooks/useMetrics'
import useMobile from '../../../hooks/useMobile'
import useNotification from '../../../hooks/useNotification'
import useFirebaseMessaging from '../../../hooks/useFirebaseMessaging'
import listRoutes from '../../../routes/listRoutes'
import AuthService from '../../../modules/Auth/services/auth'
import sendGaPush from '../../../utils/sendGaPush'
import Loading from '../../atoms/loading/loading'
import CardClosed from '../../molecules/cards/card-closed/card-closed'
import Header from '../header/header'
import NavMenu from '../nav-menu/nav-menu'
import SlideOverlay from '../slide-overlay/slide-overlay'
import useUIActions from 'hooks/useUIActions'
import Modal from 'components/atoms/modal/modal'
import Button from 'components/atoms/button/button'

const Layout = props => {
	const {
		children,
		header = true,
		showMenu = true,
		backgroundClass = '',
		chat = true,
		transparentHeader = false,
		hideNotification = false
	} = props

	const {
		session: { isLogin },
		elementsUI: { modalActive, popupTopActive, showLoading, showOverlay, hideNavBar }
	} = useSelector(state => state)

	const { pathname } = useLocation()
	const isMobile = useMobile()
	const timerRef = useRef(null)

	const {
		showNotificationsOverlay,
		closeNotification,
		goToNotification,
		pendingNotification,
		showNotification
	} = useNotification({ listener: showOverlay })

	const { handleShowModal, handleCloseModal } = useUIActions()
	const { fcmToken, requestNotificationPermission, notificationPermission } = useFirebaseMessaging()

	useInstalledApp()
	useMetrics()

	const isNotificationCardVisible =
		AuthService.isLoggedIn(true) && isLogin && !transparentHeader && !hideNotification

	const handleModalClose = useCallback(() => {
		localStorage.setItem('notification_modal_shown', 'true')
		if (timerRef.current) {
			clearTimeout(timerRef.current)
			timerRef.current = null
		}
		handleCloseModal()
	}, [handleCloseModal])

	const handleAcceptNotifications = useCallback(() => {
		requestNotificationPermission()
		localStorage.setItem('notification_enabled', 'true')
		localStorage.setItem('notification_modal_shown', 'true')
		if (timerRef.current) {
			clearTimeout(timerRef.current)
			timerRef.current = null
		}
		handleCloseModal()
	}, [requestNotificationPermission, handleCloseModal])

	useEffect(() => {
		if (pathname !== '/tus-servicios' || !AuthService.isLoggedIn(true) || !isLogin) {
			return
		}

		const hasShownModal = localStorage.getItem('notification_modal_shown') === 'true'
		const hasNotificationEnabled = localStorage.getItem('notification_enabled') === 'true'

		if (
			!hasShownModal &&
			!hasNotificationEnabled &&
			notificationPermission !== 'granted' &&
			!fcmToken
		) {
			timerRef.current = setTimeout(() => {
				handleShowModal(
					<Modal onClose={handleModalClose}>
						<p className='pb2'>
							¿Desea recibir notificaciones para mantenerse informado sobre importantes
							actualizaciones y ofertas?
						</p>
						<Button title='Aceptar' classes='pink mb1' onClick={handleAcceptNotifications} />
						<Button title='Ahora no' onClick={handleModalClose} />
					</Modal>
				)
			}, 6000)
		}

		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current)
				timerRef.current = null
			}
		}
	}, [
		pathname,
		isLogin,
		fcmToken,
		notificationPermission,
		handleShowModal,
		handleModalClose,
		handleAcceptNotifications
	])

	const handleNotificationClick = notification => {
		sendGaPush({
			category: 'SaludContinuarediseno',
			action: 'ClicNotificacionesEnAplicacion',
			label: notification.title
		})
		goToNotification({
			url: notification?.link,
			id: notification?.id,
			setLocal: true
		})
	}

	return (
		<>
			{showLoading && <Loading />}
			{modalActive && <div className='o-modal-index'>{modalActive}</div>}
			{(header || transparentHeader) && isMobile && (
				<Header
					{...props}
					showNotificationsOverlay={showNotificationsOverlay}
					pendingNotification={pendingNotification}
				/>
			)}
			{SlideOverlay && <SlideOverlay />}
			{popupTopActive}
			<div className={`o-main-container ${header ? 'o-header-layout ' : ''} ${backgroundClass}`}>
				{isNotificationCardVisible && (
					<NotificationCard
						notification={showNotification}
						onClose={() => closeNotification({ id: showNotification.id, setLocal: true })}
						onClick={() => handleNotificationClick(showNotification)}
					/>
				)}
				{children}
			</div>
			{showMenu && !hideNavBar && isMobile && (
				<NavMenu chat={chat} isParQPublicPath={pathname.includes(listRoutes.parQPublic)} />
			)}
		</>
	)
}

const NotificationCard = ({ notification, onClose, onClick }) => (
	<div
		className={`o-main-container__notification container pt1 pb1 ${
			notification ? 'open' : 'close'
		}`}
	>
		<CardClosed {...notification} onClose={onClose} onClick={onClick} />
	</div>
)

export default Layout
